import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useVenueAuth } from "../contexts/VenueAuthContext";
import { useVenue } from "../contexts/VenueContext";
import { useCategories } from "../hooks/useCategories";

export default function VenueSwitcher() {
  const { profile } = useVenueAuth();
  const { venues, currentVenue, switchVenue } = useVenue();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { categories } = useCategories();
  const [open, setOpen] = useState(false);

  const categoryLabel = (key: string) =>
    categories.find((c) => c.key === key)?.label ?? key;

  if (!currentVenue) return null;

  // Only show active venues in the switcher dropdown (exclude pending)
  const switchableVenues = venues.filter((v) => v.status === "active");

  // Single switchable venue and not owner — just show name, no dropdown
  if (switchableVenues.length <= 1 && profile?.role !== "venue_owner") {
    return (
      <div className="flex items-center gap-2">
        {currentVenue.logo_url ? (
          <img
            src={
              currentVenue.logo_url.startsWith("http")
                ? currentVenue.logo_url
                : `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/venues/${currentVenue.logo_url}`
            }
            alt={currentVenue.name}
            className="h-8 w-8 flex-shrink-0 rounded-lg object-cover"
          />
        ) : (
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-100 text-sm font-bold text-indigo-600">
            {currentVenue.name.charAt(0)}
          </div>
        )}
        <div className="min-w-0">
          <p className="hidden max-w-[160px] truncate text-[14px] font-bold leading-tight text-gray-900 min-[440px]:block sm:max-w-none sm:text-sm">
            {currentVenue.name}
          </p>
          <p className="hidden min-[440px]:block text-xs capitalize text-gray-500">
            {profile?.role?.replace("_", " ")}
          </p>
        </div>
      </div>
    );
  }

  // Venue owner or multi-venue — dropdown
  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        data-testid="venue-selector"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={`Switch venue, current: ${currentVenue.name}`}
        className="flex items-center gap-2 rounded-lg px-2 py-1 transition-colors hover:bg-gray-100"
      >
        {currentVenue.logo_url ? (
          <img
            data-testid="venue-logo"
            src={
              currentVenue.logo_url.startsWith("http")
                ? currentVenue.logo_url
                : `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/venues/${currentVenue.logo_url}`
            }
            alt={currentVenue.name}
            className="h-8 w-8 flex-shrink-0 rounded-lg object-cover"
          />
        ) : (
          <div
            data-testid="venue-logo"
            className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-100 text-sm font-bold text-indigo-600"
          >
            {currentVenue.name.charAt(0)}
          </div>
        )}
        <div className="min-w-0 text-left">
          <p className="hidden max-w-[160px] truncate text-[14px] font-bold leading-tight text-gray-900 min-[440px]:block sm:max-w-none sm:text-sm">
            {currentVenue.name}
          </p>
          <p className="hidden min-[440px]:block text-xs capitalize text-gray-500">
            {profile?.role?.replace("_", " ")}
          </p>
        </div>
        <svg
          className={`h-4 w-4 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {open && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          {/* Dropdown */}
          <div
            role="listbox"
            aria-label="Select venue"
            className="absolute left-0 top-full z-20 mt-1 max-h-[calc(80vh)] w-64 overflow-y-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
          >
            <p className="sticky top-0 bg-white px-3 py-1.5 text-xs font-medium uppercase tracking-wider text-gray-400">
              Switch venue
            </p>
            {switchableVenues.map((venue) => (
              <button
                key={venue.id}
                role="option"
                aria-selected={venue.id === currentVenue.id}
                onClick={() => {
                  if (
                    pathname.endsWith("/edit") &&
                    !window.confirm("Switching venue will discard unsaved changes. Continue?")
                  ) {
                    return;
                  }
                  switchVenue(venue.id);
                  setOpen(false);
                  // Deal pages belong to the previous venue
                  if (/^\/deals\/(?!new$)[^/]+/.test(pathname)) navigate("/deals");
                }}
                className={`flex w-full items-center gap-3 px-3 py-2 text-left transition-colors hover:bg-gray-50 ${
                  venue.id === currentVenue.id ? "bg-indigo-50" : ""
                }`}
              >
                {venue.logo_url ? (
                  <img
                    src={
                      venue.logo_url.startsWith("http")
                        ? venue.logo_url
                        : `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/venues/${venue.logo_url}`
                    }
                    alt={venue.name}
                    className="h-8 w-8 flex-shrink-0 rounded-lg object-cover"
                  />
                ) : (
                  <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-indigo-100 text-sm font-bold text-indigo-600">
                    {venue.name.charAt(0)}
                  </div>
                )}
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-gray-900">
                    {venue.name}
                  </p>
                  <p className="truncate text-xs text-gray-500">
                    {categoryLabel(venue.category)} —{" "}
                    <span className="capitalize">{venue.status}</span>
                  </p>
                </div>
                {venue.id === currentVenue.id && (
                  <svg
                    className="ml-auto h-4 w-4 flex-shrink-0 text-indigo-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                )}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
